import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { catchError, finalize, of, switchMap, tap } from 'rxjs';

import { IPromotionDetail, PromotionsService } from 'core';
import { GoBackComponent, KorviuButtonComponent, KorviuLoaderComponent, KorviuNoDataComponent } from 'shared';
import { PromotionDetailWinnersModalComponent } from '../promotion-detail-winners-modal/promotion-detail-winners-modal.component';
import { PromotionEligibilityModalComponent } from '../promotion-eligibility-modal/promotion-eligibility-modal.component';
import { PromotionThresholdWarningModalComponent } from '../promotion-threshold-warning-modal/promotion-threshold-warning-modal.component';
import { PromotionDetailTab, getPromotionDetailState } from './promotion-detail-state';

interface IPromotionDetailRow {
  labelKey: string;
  value: string;
}

type PromotionRewardIconName = 'first-place' | 'second-place' | 'third-place';
type PromotionStateBannerVariant = 'participating' | 'max' | 'lost' | 'won' | 'claimed';

interface IPromotionRewardRow extends IPromotionDetailRow {
  iconName: PromotionRewardIconName;
}

interface IPromotionInstantRewardTile {
  id: string;
  labelKey: string;
  value: string;
}

@Component({
  selector: 'app-promotion-details',
  imports: [
    GoBackComponent,
    KorviuButtonComponent,
    KorviuLoaderComponent,
    KorviuNoDataComponent,
    NgTemplateOutlet,
    RouterLink,
    TranslatePipe
  ],
  templateUrl: './promotion-details.component.html',
  styleUrl: './promotion-details.component.scss'
})
export class PromotionDetailsComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #promotionsService = inject(PromotionsService);
  readonly #route = inject(ActivatedRoute);
  readonly #translateService = inject(TranslateService);
  readonly #modalService = inject(NzModalService);

  readonly tabs: { id: PromotionDetailTab; labelKey: string }[] = [
    { id: 'details', labelKey: 'promotions.detail.tabs.details' },
    { id: 'entries', labelKey: 'promotions.detail.tabs.entries' },
    { id: 'conditions', labelKey: 'promotions.detail.tabs.conditions' }
  ];
  readonly ticketSlots = [1, 2, 3, 4, 5, 6, 7] as const;
  readonly promotion = signal<IPromotionDetail | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly selectedTab = signal<PromotionDetailTab>('details');
  readonly ctaFeedback = signal<string>('');
  readonly instantRewardRevealed = signal<boolean>(false);
  readonly currentLang = signal<string>(
    this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
  );
  readonly detailState = computed(() => {
    const promotion = this.promotion();

    return promotion ? getPromotionDetailState(promotion, this.selectedTab()) : null;
  });
  readonly activeTabContentType = computed(() => this.detailState()?.activeTabContentType ?? 'details');
  readonly ctaLabel = computed(() => {
    const promotion = this.promotion();
    const labelKey = this.detailState()?.cta.labelKey;

    if (!promotion) {
      return '';
    }

    if (!labelKey) {
      return '';
    }

    return this.#translate(labelKey, { count: promotion.tickets });
  });
  readonly heroMessage = computed(() => {
    const promotion = this.promotion();
    const messageKey = this.detailState()?.heroMessageKey;

    if (!promotion || !messageKey) {
      return '';
    }

    const maxEntries = this.#getMaximumEntries(promotion);
    const maxParticipants = promotion.max_participants ?? 50;

    return this.#translate(messageKey, {
      count: promotion.user_entries ?? promotion.applied_entries ?? 0,
      maxEntries,
      maxParticipants
    });
  });
  readonly statusLabel = computed(() => {
    const labelKey = this.detailState()?.statusLabelKey;

    return labelKey ? this.#translate(labelKey) : '';
  });
  readonly costLabel = computed(() => {
    return this.#getCountLabel('promotions.tickets', 'promotions.tickets_other', this.promotion()?.tickets ?? 0);
  });
  readonly enterLabel = computed(() => {
    return this.#getCountLabel(
      'promotions.detail.enterForTicket',
      'promotions.detail.enterForTicket_other',
      this.promotion()?.tickets ?? 0
    );
  });
  readonly endDateLabel = computed(() => {
    const endDate = this.promotion()?.end_date;

    if (!endDate) {
      return '';
    }

    return this.#getTimeRemainingLabel(endDate);
  });
  readonly entriesMessage = computed(() => {
    const maxEntries = (this.promotion()?.available_entries ?? 0) + 5;
    const maxParticipants = this.promotion()?.max_participants ?? 50;

    return this.#translate('promotions.detail.entriesMessage', { maxEntries, maxParticipants });
  });
  readonly userEntriesLabel = computed(() => {
    return this.#getCountLabel(
      'promotions.appliedEntries',
      'promotions.appliedEntries_other',
      this.promotion()?.user_entries ?? this.promotion()?.applied_entries ?? 0
    );
  });
  readonly entriesChanceLabel = computed(() => {
    const promotion = this.promotion();
    const count = promotion?.user_entries ?? promotion?.applied_entries ?? 0;
    const maxEntries = promotion?.entries_total ?? promotion?.max_participants ?? 50;

    return this.#translate('promotions.detail.entriesChance', { count, maxEntries });
  });
  readonly shouldShowStateBanner = computed(() => {
    const state = this.detailState();

    return Boolean(
      state?.isParticipating || state?.isMaxEntriesUsed || state?.isLost || state?.isWinner || state?.isRewardLadder
    );
  });
  readonly isInstantReward = computed(() => this.promotion()?.promotion_kind === 'instant_reward');
  readonly shouldShowInstantRewardTiles = computed(() => {
    const state = this.detailState();

    return Boolean(this.isInstantReward() && (this.instantRewardRevealed() || state?.isWinner));
  });
  readonly shouldShowWinnersBlock = computed(() => {
    const promotion = this.promotion();

    return Boolean(
      promotion &&
        promotion.promotion_kind !== 'instant_reward' &&
        promotion.promotion_kind !== 'prize_ladder' &&
        this.activeTabContentType() === 'details' &&
        this.topWinnerRows().length
    );
  });
  readonly availableTicketsCount = computed(() => {
    const promotion = this.promotion();

    return promotion?.available_tickets ?? promotion?.available_entries ?? 0;
  });
  readonly filledTicketCount = computed(() => {
    const promotion = this.promotion();

    return Math.min(this.ticketSlots.length, promotion?.user_entries ?? promotion?.applied_entries ?? 0);
  });
  readonly stateBannerTitleKey = computed(() => {
    const promotion = this.promotion();
    const state = this.detailState();

    if (!promotion || !state) {
      return '';
    }

    if (promotion.result_state === 'won_claimed') {
      return 'promotions.detail.stateBanner.claimedTitle';
    }

    if (promotion.result_state === 'won_unclaimed') {
      return promotion.promotion_kind === 'prize_ladder'
        ? 'promotions.detail.stateBanner.completedTitle'
        : 'promotions.detail.stateBanner.wonTitle';
    }

    if (state.isLost) {
      return 'promotions.detail.stateBanner.lostTitle';
    }

    if (state.isMaxEntriesUsed) {
      return 'promotions.detail.stateBanner.maxTitle';
    }

    return 'promotions.detail.stateBanner.participatingTitle';
  });
  readonly stateBannerVariant = computed<PromotionStateBannerVariant>(() => {
    const promotion = this.promotion();
    const state = this.detailState();

    if (promotion?.result_state === 'won_claimed') {
      return 'claimed';
    }

    if (promotion?.result_state === 'won_unclaimed') {
      return 'won';
    }

    if (state?.isLost) {
      return 'lost';
    }

    if (state?.isMaxEntriesUsed) {
      return 'max';
    }

    return 'participating';
  });
  readonly progressPercent = computed(() => {
    const promotion = this.promotion();

    if (!promotion?.rewards_total) {
      return 0;
    }

    if (promotion.reward_progress_percent !== null && promotion.reward_progress_percent !== undefined) {
      return Math.max(0, Math.min(100, promotion.reward_progress_percent));
    }

    if (promotion.promotion_kind === 'prize_ladder' && promotion.result_state !== 'active') {
      return 100;
    }

    return Math.max(0, Math.min(100, (promotion.rewards_left / promotion.rewards_total) * 100));
  });
  readonly progressLabel = computed(() => {
    const promotion = this.promotion();

    if (!promotion) {
      return '';
    }

    if (promotion.promotion_kind === 'prize_ladder') {
      if (promotion.result_state !== 'active') {
        return this.#translate('promotions.detail.stateBanner.completedTitle');
      }

      return this.rewardLadderAvailabilityLabel();
    }

    return this.#translate('promotions.detail.rewardsLeft', { count: promotion.rewards_left });
  });
  readonly shouldShowLadderParticipationProgress = computed(() => {
    const promotion = this.promotion();

    return Boolean(
      promotion?.promotion_kind === 'prize_ladder' &&
        promotion.result_state === 'active' &&
        promotion.participation_state === 'participating' &&
        promotion.ladder_progress_label
    );
  });
  readonly detailRows = computed<IPromotionDetailRow[]>(() => {
    const promotion = this.promotion();

    if (!promotion) {
      return [];
    }

    return [
      { labelKey: 'promotions.detail.winners', value: String(promotion.winners_count) },
      ...(promotion.category ? [{ labelKey: 'promotions.detail.category', value: promotion.category }] : []),
      { labelKey: 'promotions.detail.eligibleLocation', value: promotion.eligible_location },
      { labelKey: 'promotions.detail.duration', value: this.#getDurationLabel(promotion) },
      {
        labelKey: 'promotions.detail.entryAvailability',
        value: this.#getTranslatedDetailValue(promotion.entry_availability)
      }
    ];
  });
  readonly tabContentTitleKey = computed(() => {
    switch (this.activeTabContentType()) {
      case 'entries':
        return 'promotions.detail.entriesTab.title';
      case 'claim_instructions':
        return 'promotions.detail.claimTab.title';
      case 'conditions':
        return 'promotions.detail.conditionsTab.title';
      case 'details':
      default:
        return 'promotions.detail.promotionDetails';
    }
  });
  readonly tabRows = computed<IPromotionDetailRow[]>(() => {
    const promotion = this.promotion();

    if (!promotion) {
      return [];
    }

    switch (this.activeTabContentType()) {
      case 'entries':
        return this.#mapDetailRows(promotion.entry_rows);
      case 'claim_instructions':
        return this.#mapDetailRows(promotion.claim_instruction_rows);
      case 'conditions':
        return this.#mapDetailRows(promotion.condition_rows);
      case 'details':
      default:
        return this.detailRows();
    }
  });
  readonly rewardRows = computed<IPromotionDetailRow[]>(() => this.#mapDetailRows(this.promotion()?.reward_rows));
  readonly instantRewardTiles = computed<IPromotionInstantRewardTile[]>(() => {
    const rows = this.claimRewardRows().length ? this.claimRewardRows() : this.rewardRows();

    if (rows.length) {
      return rows.map(row => ({
        id: row.labelKey,
        labelKey: row.labelKey,
        value: row.value
      }));
    }

    return [
      {
        id: 'ganas',
        labelKey: 'promotions.detail.instantReward.ganasReward',
        value: '500 Ganas'
      },
      {
        id: 'ticket',
        labelKey: 'promotions.detail.instantReward.ticketReward',
        value: this.#getCountLabel('promotions.tickets', 'promotions.tickets_other', 1)
      },
      {
        id: 'bonus',
        labelKey: 'promotions.detail.instantReward.bonusReward',
        value: this.#getCountLabel('promotions.tickets', 'promotions.tickets_other', 2)
      }
    ];
  });
  readonly rewardDisplayRows = computed<IPromotionRewardRow[]>(() => {
    return this.rewardRows().map((row, index) => ({
      ...row,
      iconName: this.#getRewardIconName(index)
    }));
  });
  readonly primaryRewardRow = computed<IPromotionRewardRow | null>(() => this.rewardDisplayRows()[0] ?? null);
  readonly secondaryRewardRows = computed<IPromotionRewardRow[]>(() => this.rewardDisplayRows().slice(1, 3));
  readonly rewardLadderAvailabilityLabel = computed(() => {
    const promotion = this.promotion();

    if (!promotion || promotion.promotion_kind !== 'prize_ladder' || promotion.rewards_left <= 0) {
      return '';
    }

    return this.#translate('promotions.detail.rewardTab.onlyLeft', { count: promotion.rewards_left });
  });
  readonly claimRewardRows = computed<IPromotionDetailRow[]>(() => {
    const promotion = this.promotion();

    if (!promotion?.claim_reward_rows?.length) {
      return [];
    }

    return this.#mapDetailRows(promotion.claim_reward_rows);
  });
  readonly topWinnerRows = computed(() => (this.promotion()?.winner_rows ?? []).slice(0, 3));

  public ngOnInit(): void {
    this.#translateService.onLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(event => this.currentLang.set(event.lang));

    this.#route.paramMap
      .pipe(
        switchMap(params => {
          this.isLoading.set(true);
          const promotionId = Number(params.get('promotionId'));

          return this.#promotionsService.getPromotionDetail(Number.isFinite(promotionId) ? promotionId : -1).pipe(
            catchError(() => {
              return of(null);
            }),
            finalize(() => {
              this.isLoading.set(false);
            })
          );
        }),
        tap(promotion => {
          this.promotion.set(promotion);
          this.ctaFeedback.set('');
          this.instantRewardRevealed.set(
            Boolean(promotion?.promotion_kind === 'instant_reward' && promotion.result_state !== 'active')
          );
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public onTabClick(tab: PromotionDetailTab): void {
    this.selectedTab.set(tab);
  }

  public onCtaClick(): void {
    const promotion = this.promotion();
    const state = this.detailState();

    if (!promotion || state?.cta.disabled) {
      return;
    }

    if (promotion.eligibility_warning) {
      this.#openWarningModal(promotion);
      return;
    }

    if (promotion.result_state === 'won_unclaimed') {
      if (promotion.claim_requires_tax_info) {
        this.#openThresholdWarningModal();
        return;
      }

      this.#claimReward(promotion);
      return;
    }

    if (promotion.promotion_kind === 'instant_reward') {
      this.onInstantRewardTap();
      return;
    }
  }

  public onInstantRewardTap(): void {
    const promotion = this.promotion();

    if (!promotion || promotion.promotion_kind !== 'instant_reward') {
      return;
    }

    this.instantRewardRevealed.set(true);
    this.promotion.update(current => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        participation_state: 'participating',
        result_state: 'won_unclaimed',
        user_entries: Math.max(1, current.user_entries ?? current.applied_entries ?? 0),
        applied_entries: Math.max(1, current.applied_entries ?? current.user_entries ?? 0),
        claim_reward_rows: current.claim_reward_rows?.length
          ? current.claim_reward_rows
          : [{ id: 'reward', label: 'promotions.detail.claimTab.reward', value: '500 Ganas' }]
      };
    });
    this.ctaFeedback.set(this.#translate('promotions.detail.instantReward.revealed'));
  }

  public dismissCtaFeedback(): void {
    this.ctaFeedback.set('');
  }

  public onViewAllPrizesClick(): void {
    this.ctaFeedback.set(this.#translate('promotions.detail.ctaFeedback.allPrizesShown'));
  }

  #openWarningModal(promotion: IPromotionDetail): void {
    if (promotion.eligibility_warning === 'tax_threshold') {
      this.#openThresholdWarningModal();
      return;
    }

    this.#modalService.create({
      nzContent: PromotionEligibilityModalComponent,
      nzData: {
        variant: promotion.eligibility_warning,
        eligibleLocation: promotion.eligible_location,
        accountLocation: 'United States'
      },
      nzCentered: true,
      nzClosable: false,
      nzFooter: null,
      nzWidth: 'auto',
      nzBodyStyle: { padding: '0', background: 'transparent' },
      nzWrapClassName: 'promotion-modal-wrap',
      nzClassName: 'promotion-eligibility-modal-dialog'
    });
  }

  #openThresholdWarningModal(): void {
    this.#modalService.create({
      nzContent: PromotionThresholdWarningModalComponent,
      nzCentered: true,
      nzClosable: false,
      nzFooter: null,
      nzWidth: 'auto',
      nzBodyStyle: { padding: '0', background: 'transparent' },
      nzWrapClassName: 'promotion-modal-wrap',
      nzClassName: 'promotion-threshold-warning-modal-dialog'
    });
  }

  #claimReward(promotion: IPromotionDetail): void {
    this.promotion.update(current =>
      current
        ? {
            ...current,
            result_state: 'won_claimed',
            claim_info: current.claim_info ?? 'promotions.detail.rewardClaimed'
          }
        : current
    );
    this.selectedTab.set('conditions');
    this.ctaFeedback.set(this.#translate('promotions.detail.ctaFeedback.rewardClaimed'));
  }

  public openWinnersModal(): void {
    const promotion = this.promotion();

    if (!promotion) {
      return;
    }

    this.#modalService.create({
      nzContent: PromotionDetailWinnersModalComponent,
      nzData: {
        promotionName: promotion.title,
        winners: promotion.winner_rows ?? []
      },
      nzCentered: true,
      nzClosable: false,
      nzFooter: null,
      nzWidth: 'auto',
      nzBodyStyle: { padding: '0', background: 'transparent' },
      nzWrapClassName: 'promotion-modal-wrap',
      nzClassName: 'promotion-winners-modal-dialog'
    });
  }

  #getDurationLabel(promotion: IPromotionDetail): string {
    if (!promotion.start_date || !promotion.end_date) {
      return '';
    }

    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return '';
    }

    return `${this.#formatShortDate(startDate)} - ${this.#formatShortDate(endDate)}`;
  }

  #formatShortDate(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).format(date);
  }

  #getTimeRemainingLabel(endDate: string): string {
    const endTimestamp = new Date(endDate).getTime();

    if (Number.isNaN(endTimestamp)) {
      return '';
    }

    const diffInMs = endTimestamp - Date.now();
    const hourInMs = 1000 * 60 * 60;
    const dayInMs = hourInMs * 24;

    if (diffInMs <= 0) {
      return this.#translate('promotions.expired');
    }

    const totalDays = Math.floor(diffInMs / dayInMs);
    if (totalDays >= 1) {
      return this.#getCountLabel('promotions.daysLeft', 'promotions.daysLeft_other', totalDays);
    }

    const totalHours = Math.floor(diffInMs / hourInMs);
    if (totalHours >= 1) {
      return this.#getCountLabel('promotions.hoursLeft', 'promotions.hoursLeft_other', totalHours);
    }

    return this.#translate('promotions.lessThanHourLeft');
  }

  #getCountLabel(singularKey: string, pluralKey: string, count: number): string {
    return this.#translate(count === 1 ? singularKey : pluralKey, { count });
  }

  #getMaximumEntries(promotion: IPromotionDetail): number {
    return (promotion.entries_per_user ?? 0) + (promotion.available_entries ?? 0);
  }

  #getTranslatedDetailValue(value: string): string {
    return value.startsWith('promotions.') ? this.#translate(value) : value;
  }

  #translate(key: string, params?: Record<string, unknown>): string {
    this.currentLang();

    return this.#translateService.instant(key, params);
  }

  #mapDetailRows(rows: IPromotionDetail['entry_rows']): IPromotionDetailRow[] {
    return (rows ?? []).map(row => ({
      labelKey: row.label,
      value: this.#getTranslatedDetailValue(row.value)
    }));
  }

  #getRewardIconName(index: number): PromotionRewardIconName {
    switch (index) {
      case 1:
        return 'second-place';
      case 2:
        return 'third-place';
      case 0:
      default:
        return 'first-place';
    }
  }
}
